import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './services/queryClient';
import { useDashboardStore } from './store/dashboardStore';
import { ToastProvider, ErrorBoundary } from './components/ui';
import InactivityTimerProvider from './providers/InactivityTimerProvider';
import AppLayout from './layouts/AppLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AuditLogs from './pages/AuditLogs';
import UserManagement from './pages/UserManagement';
import UserProfile from './pages/UserProfile';
import ComplianceDashboard from './pages/ComplianceDashboard';
import NotificationSettings from './pages/NotificationSettings';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import DocumentsUnified from './pages/documents/DocumentsUnified';
import Evidence from './pages/Evidence';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import PublicCertificateVerification from './pages/PublicCertificateVerification';
import CertificateVerification from './pages/CertificateVerification';
import VerifyCertificateForm from './pages/VerifyCertificateForm';
import CertBodies from './pages/CertBodies';
import ClientManagement from './pages/ClientManagement';
import ClientProfile from './pages/ClientProfile';
import SLAManagement from './pages/SLAManagement';
import Invoices from './pages/Invoices';
import CertificatesManager from './pages/CertificatesManager';
import CPDCertificates from './pages/CPDCertificates';
import CompanyAssessmentCertificates from './pages/CompanyAssessmentCertificates';
import ISOCertificates from './pages/ISOCertificates';
import CertificateLifecycleDashboard from './pages/CertificateLifecycleDashboard';
import PricingOverview from './pages/PricingOverview';
import Settings from './pages/Settings';
import AccreditationDashboard from './pages/AccreditationDashboard';
import ManageCBAccreditations from './pages/ManageCBAccreditations';
import CBAccreditationDetail from './pages/CBAccreditationDetail';
import MyCBAccreditation from './pages/MyCBAccreditation';
import ClientAccreditationView from './pages/ClientAccreditationView';
import ManageAuditors from './pages/ManageAuditors';
import TrainerAssessors from './pages/TrainerAssessors';
import TrainerAssessorDetail from './pages/TrainerAssessorDetail';
import ManageCourses from './pages/ManageCourses';
import SurveillanceDashboard from './pages/SurveillanceDashboard';
import AccreditationScopes from './pages/AccreditationScopes';
import AccreditationRules from './pages/AccreditationRules';
import AccreditationRenewals from './pages/AccreditationRenewals';
import AccreditationCertificates from './pages/AccreditationCertificates';
import AuditorPerformance from './pages/AuditorPerformance';
import CourseAccreditations from './pages/CourseAccreditations';
import AccreditationEvidence from './pages/AccreditationEvidence';
import AccreditationDecisionPanel from './pages/AccreditationDecisionPanel';
import AccreditationMonitoring from './pages/AccreditationMonitoring';
import MembershipDashboard from './pages/MembershipDashboard';
import MembershipApplication from './pages/MembershipApplication';
import MembershipDecisionPanel from './pages/MembershipDecisionPanel';
import CompetencyCourses from './pages/CompetencyCourses';
import CompetencyCourseDetail from './pages/CompetencyCourseDetail';
import CompetencyDashboard from './pages/CompetencyDashboard';
import CompetencyCoursePlayer from './pages/CompetencyCoursePlayer';
import CompetencyExam from './pages/CompetencyExam';
import CourseManager from './components/CourseManager';
import CourseEditor from './components/CourseEditor';
import ModuleEditor from './components/ModuleEditor';
import MyCourses from './components/MyCourses';
import CourseViewer from './components/CourseViewer';
import AllowedCourses from './components/AllowedCourses';
import AssignCourses from './components/AssignCourses';
import AdminAnalytics from './components/AdminAnalytics';
import ScormImporter from './components/ScormImporter';
import ReviewerDashboard from './pages/ReviewerDashboard';
import EvidenceReview from './pages/EvidenceReview';
import AuditFeedback from './pages/AuditFeedback';
import MyAuditFeedback from './pages/MyAuditFeedback';
import StaffCompetency from './pages/StaffCompetency';
import ShopManagement from './pages/ShopManagement';
import AccreditationStore from './pages/AccreditationStore';
import AuditCalendar from './pages/AuditCalendar';
import CAPAManagement from './pages/CAPAManagement';
import CBApplicationPortal from './pages/CBApplicationPortal';
import CBApplicationReview from './pages/CBApplicationReview';
import EmailSettings from './pages/EmailSettings';
import AIEvidenceReview from './pages/AIEvidenceReview';
import RemoteAuditSupport from './pages/RemoteAuditSupport';
import WebhookManagement from './pages/WebhookManagement';
import './App.css';

// ── Guards ────────────────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return children;
}

function RoleGuard({ allowedRoles, children }) {
  const role = localStorage.getItem('userRole');
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// ── App ────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <InactivityTimerProvider>
              <div className="App">
                <Routes>
                  {/* ── Public routes ── */}
                  <Route path="/login"          element={<Login />} />
                  <Route path="/reset-password" element={<Login />} />
                  <Route path="/privacy"        element={<Privacy />} />
                  <Route path="/terms"    element={<Terms />} />
                  <Route path="/pricing"  element={<PricingOverview />} />
                  <Route path="/certificate/:certificateId" element={<PublicCertificateVerification />} />
                  <Route path="/verify"              element={<VerifyCertificateForm />} />
                  <Route path="/verify/:certificateId" element={<CertificateVerification />} />

                  {/* ── Protected routes — wrapped in AppLayout ── */}
                  <Route path="/*" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Routes>
                          {/* Dashboard */}
                          <Route path="/" element={<Dashboard />} />

                          {/* Tools */}
                          <Route path="/compliance"   element={<ComplianceDashboard />} />
                          <Route path="/documents"    element={<DocumentsUnified />} />
                          <Route path="/evidence"     element={<Evidence />} />

                          {/* Profile */}
                          <Route path="/profile"       element={<UserProfile />} />
                          <Route path="/notifications" element={<NotificationSettings />} />

                          {/* Support */}
                          <Route path="/tickets"     element={<Tickets />} />
                          <Route path="/tickets/:id" element={<TicketDetail />} />

                          {/* Certificates */}
                          <Route path="/certificates"          element={<CertificatesManager />} />
                          <Route path="/certificate-lifecycle" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><CertificateLifecycleDashboard /></RoleGuard>} />
                          <Route path="/iso-certificates"      element={<ISOCertificates />} />
                          <Route path="/cpd-certificates"      element={<CPDCertificates />} />
                          <Route path="/company-assessments"   element={<CompanyAssessmentCertificates />} />

                          {/* Operations */}
                          <Route path="/clients"           element={<RoleGuard allowedRoles={['super_admin', 'certification_body', 'third_party_admin']}><ClientManagement /></RoleGuard>} />
                          <Route path="/clients/:clientId" element={<RoleGuard allowedRoles={['super_admin', 'certification_body', 'third_party_admin']}><ClientProfile /></RoleGuard>} />
                          <Route path="/cert-bodies"    element={<RoleGuard allowedRoles={['super_admin']}><CertBodies /></RoleGuard>} />
                          <Route path="/sla-management" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><SLAManagement /></RoleGuard>} />
                          <Route path="/invoices"       element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><Invoices /></RoleGuard>} />

                          {/* Accreditation Management */}
                          <Route path="/accreditation" element={<RoleGuard allowedRoles={['super_admin']}><AccreditationDashboard /></RoleGuard>} />
                          <Route path="/accreditation/cert-bodies" element={<RoleGuard allowedRoles={['super_admin']}><ManageCBAccreditations /></RoleGuard>} />
                          <Route path="/accreditation/cert-bodies/:accreditationId" element={<RoleGuard allowedRoles={['super_admin']}><CBAccreditationDetail /></RoleGuard>} />
                          <Route path="/accreditation/auditors" element={<RoleGuard allowedRoles={['super_admin']}><ManageAuditors /></RoleGuard>} />
                          <Route path="/accreditation/trainer-assessors" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><TrainerAssessors /></RoleGuard>} />
                          <Route path="/accreditation/trainer-assessors/:id" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><TrainerAssessorDetail /></RoleGuard>} />
                          <Route path="/accreditation/courses"  element={<RoleGuard allowedRoles={['super_admin']}><ManageCourses /></RoleGuard>} />
                          <Route path="/accreditation/course-accreditations" element={<RoleGuard allowedRoles={['super_admin']}><CourseAccreditations /></RoleGuard>} />
                          <Route path="/accreditation/course-accreditations/:accredId/evidence" element={<RoleGuard allowedRoles={['super_admin']}><AccreditationEvidence /></RoleGuard>} />
                          <Route path="/accreditation/course-accreditations/:accredId/decision" element={<RoleGuard allowedRoles={['super_admin']}><AccreditationDecisionPanel /></RoleGuard>} />
                          <Route path="/accreditation/monitoring" element={<RoleGuard allowedRoles={['super_admin']}><AccreditationMonitoring /></RoleGuard>} />

                          {/* Membership */}
                          <Route path="/membership" element={<RoleGuard allowedRoles={['super_admin']}><MembershipDashboard /></RoleGuard>} />
                          <Route path="/membership/applications/:id" element={<RoleGuard allowedRoles={['super_admin']}><MembershipApplication /></RoleGuard>} />
                          <Route path="/membership/applications/:id/decision" element={<RoleGuard allowedRoles={['super_admin']}><MembershipDecisionPanel /></RoleGuard>} />

                          <Route path="/accreditation/scopes"   element={<RoleGuard allowedRoles={['super_admin']}><AccreditationScopes /></RoleGuard>} />
                          <Route path="/accreditation/rules"    element={<RoleGuard allowedRoles={['super_admin']}><AccreditationRules /></RoleGuard>} />

                          {/* Governance (Phase 2) */}
                          <Route path="/governance/surveillance"        element={<RoleGuard allowedRoles={['super_admin']}><SurveillanceDashboard /></RoleGuard>} />
                          <Route path="/governance/renewals"            element={<RoleGuard allowedRoles={['super_admin']}><AccreditationRenewals /></RoleGuard>} />
                          <Route path="/governance/acc-certificates"    element={<RoleGuard allowedRoles={['super_admin']}><AccreditationCertificates /></RoleGuard>} />
                          <Route path="/governance/auditor-performance" element={<RoleGuard allowedRoles={['super_admin']}><AuditorPerformance /></RoleGuard>} />

                          {/* CB Self-Service */}
                          <Route path="/my-accreditation"       element={<MyCBAccreditation />} />
                          <Route path="/cb-accreditation-status" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><ClientAccreditationView /></RoleGuard>} />
                          <Route path="/reviewer-dashboard"     element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><ReviewerDashboard /></RoleGuard>} />

                          {/* Learning (LMS) */}
                          <Route path="/courses"                    element={<RoleGuard allowedRoles={['super_admin']}><CourseManager /></RoleGuard>} />
                          <Route path="/courses/:courseId"          element={<RoleGuard allowedRoles={['super_admin']}><CourseEditor /></RoleGuard>} />
                          <Route path="/courses/:courseId/modules/:moduleId" element={<RoleGuard allowedRoles={['super_admin']}><ModuleEditor /></RoleGuard>} />
                          <Route path="/courses/:courseId/view"     element={<CourseViewer />} />
                          <Route path="/my-courses"                 element={<MyCourses />} />
                          <Route path="/allowed-courses"            element={<RoleGuard allowedRoles={['third_party_admin', 'certification_body']}><AllowedCourses /></RoleGuard>} />
                          <Route path="/assign-courses"             element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin', 'certification_body', 'client']}><AssignCourses /></RoleGuard>} />
                          <Route path="/scorm-import"               element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin', 'certification_body']}><ScormImporter /></RoleGuard>} />

                          {/* Competency */}
                          <Route path="/competency/courses"              element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><CompetencyCourses /></RoleGuard>} />
                          <Route path="/competency/courses/:courseId"    element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><CompetencyCourseDetail /></RoleGuard>} />
                          <Route path="/competency/my-courses"           element={<CompetencyDashboard />} />
                          <Route path="/competency/learn/:enrollmentId"  element={<CompetencyCoursePlayer />} />
                          <Route path="/competency/exam/:enrollmentId"   element={<CompetencyExam />} />

                          {/* Evidence Review */}
                          <Route path="/evidence-review" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><EvidenceReview /></RoleGuard>} />

                          {/* Audit Feedback */}
                          <Route path="/audit-feedback" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><AuditFeedback /></RoleGuard>} />
                          <Route path="/my-audit-feedback" element={<MyAuditFeedback />} />

                          {/* Staff Competency */}
                          <Route path="/staff-competency" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin', 'certification_body']}><StaffCompetency /></RoleGuard>} />

                          {/* Shop */}
                          <Route path="/shop/manage" element={<RoleGuard allowedRoles={['super_admin']}><ShopManagement /></RoleGuard>} />
                          <Route path="/store"       element={<AccreditationStore />} />

                          {/* Audit Calendar */}
                          <Route path="/calendar" element={<RoleGuard allowedRoles={['super_admin']}><AuditCalendar /></RoleGuard>} />

                          {/* CAPA */}
                          <Route path="/capa" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><CAPAManagement /></RoleGuard>} />

                          {/* CB Application Portal — applicant-facing */}
                          <Route path="/apply" element={<CBApplicationPortal />} />

                          {/* CB Application Review — admin */}
                          <Route path="/admin/applications" element={<RoleGuard allowedRoles={['super_admin']}><CBApplicationReview /></RoleGuard>} />

                          {/* AI Evidence Review */}
                          <Route path="/ai-review" element={<RoleGuard allowedRoles={['super_admin', 'third_party_admin']}><AIEvidenceReview /></RoleGuard>} />

                          {/* Remote Audit Support */}
                          <Route path="/remote-audit" element={<RoleGuard allowedRoles={['super_admin']}><RemoteAuditSupport /></RoleGuard>} />

                          {/* Email Settings */}
                          <Route path="/settings/email" element={<RoleGuard allowedRoles={['super_admin']}><EmailSettings /></RoleGuard>} />

                          {/* Webhook Management */}
                          <Route path="/settings/webhooks" element={<RoleGuard allowedRoles={['super_admin']}><WebhookManagement /></RoleGuard>} />

                          {/* System */}
                          <Route path="/analytics"  element={<RoleGuard allowedRoles={['super_admin']}><AdminAnalytics /></RoleGuard>} />
                          <Route path="/audit-logs" element={<RoleGuard allowedRoles={['super_admin']}><AuditLogs /></RoleGuard>} />
                          <Route path="/users"      element={<RoleGuard allowedRoles={['super_admin']}><UserManagement /></RoleGuard>} />
                          <Route path="/settings"   element={<RoleGuard allowedRoles={['super_admin']}><Settings /></RoleGuard>} />
                        </Routes>
                      </AppLayout>
                    </ProtectedRoute>
                  } />
                </Routes>
              </div>
            </InactivityTimerProvider>
          </Router>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
